import {
  HomeRounded as HomeIcon,
  Groups as AffiliateIcon,
  PhoneAndroidRounded as MobileAppIcon,
  RecordVoiceOverRounded as VoiceCloningIconSidebar,
  TheatersRounded as DubbingIconMobileSidebar,
  ShoppingCartRounded as PaymentIconSidebar,
  GraphicEqRounded as TTSIconMobileSidebar,
  CodeRounded as AIVoiceAPIIcon,
  MovieFilterRounded as MovieFilterRoundedIcon,
  FormatQuoteRounded,
  CardGiftcard as ReferralIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { SMS_IDENTITY_PROVIDER } from '@src/constants';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { VBEE_CONSOLE_URL } from '@src/configs';
import routes from '@src/constants/route';
import useFeatureFlags from '@src/hooks/useFeatureFlags';

import { useCheckDubbingRoute } from './useCheckDubbingRoute';

const useSidebarRoutes = () => {
  const isDubbingRoute = useCheckDubbingRoute();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);
  const { identityProvider } = useSelector((state) => state.auth.user);
  const isSMSUser = Object.values(SMS_IDENTITY_PROVIDER).includes(
    identityProvider,
  );

  const useRenameMergeProduct = getFeatureValue(
    FEATURE_KEYS.RENAME_MERGE_PRODUCT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const projectEnabled = getFeatureValue(FEATURE_KEYS.PROJECT, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const getStudioProductName = () => {
    if (isSMSUser) return 'aivoiceStudio';
    return useRenameMergeProduct ? 'newStudioName' : 'STUDIO';
  };

  const getDubbingProductName = () => {
    if (!isSMSUser && useRenameMergeProduct) return 'newDubbingName';
    return 'DUBBING';
  };

  const sidebarRoutes = [
    {
      name: 'home',
      path: routes.HOME,
      isMobileSidebar: false,
      isDesktopSidebar: true,
      labelSidebar: 'home',
      iconSidebar: HomeIcon,
    },
    {
      name: 'convertText',
      path: routes.TTS,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: getStudioProductName(),
      iconSidebar: TTSIconMobileSidebar,
    },
    {
      name: 'dubbing',
      path: routes.DUBBING,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: getDubbingProductName(),
      iconSidebar: DubbingIconMobileSidebar,
      featureKey: FEATURE_KEYS.DUBBING,
    },
    {
      name: 'project',
      path: routes.PROJECTS,
      isDesktopSidebar: projectEnabled,
      labelSidebar: 'project',
      iconSidebar: MovieFilterRoundedIcon,
      beta: true,
      isMobileSidebar: projectEnabled,
    },
    {
      name: 'myVoice',
      path: routes.VOICE_CLONING_VOICES,
      isMobileSidebar: !isSMSUser,
      isDesktopSidebar: !isSMSUser,
      labelSidebar: 'voiceCloningLabel',
      iconSidebar: VoiceCloningIconSidebar,
    },
    {
      name: 'voiceCloning',
      path: `${routes.VOICE_CLONING_VOICES}?show-qrcode=true`,
      isMobileSidebar: false,
      isDesktopSidebar: false,
      labelSidebar: 'voiceCloningLabel',
      iconSidebar: VoiceCloningIconSidebar,
      beta: true,
    },
    {
      name: 'api',
      path: VBEE_CONSOLE_URL,
      externalUrl: true,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'API',
      iconSidebar: AIVoiceAPIIcon,
    },
    {
      name: 'buyPackage',
      path: isDubbingRoute ? routes.PAYMENT_DUBBING : routes.PAYMENT,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'buyPackage',
      iconSidebar: PaymentIconSidebar,
      dataId: 'open-view-plan',
    },
    {
      name: 'referral',
      path: routes.REFERRAL,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'referral',
      iconSidebar: ReferralIcon,
      isNewFeature: true,
      featureKey: FEATURE_KEYS.REFERRAL,
    },
    {
      name: 'affiliate',
      path: routes.AFFILIATE,
      subPaths: [routes.AFFILIATE_DETAIL],
      isMobileSidebar: false,
      isDesktopSidebar: !isSMSUser ?? true,
      labelSidebar: 'affiliate',
      iconSidebar: AffiliateIcon,
    },
    {
      name: 'mobileApp',
      path: routes.MOBILE_APP,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'mobileApp',
      iconSidebar: MobileAppIcon,
    },
    // Menu item for project detail page
    {
      name: 'project',
      path: routes.PROJECTS,
      isDesktopSidebar: true,
      labelSidebar: 'project',
      iconSidebar: MovieFilterRoundedIcon,
      isDetailProject: true,
      beta: true,
      subPaths: [routes.PROJECT_DETAIL],
    },
    {
      name: 'projectDictionary',
      isDesktopSidebar: true,
      labelSidebar: 'projectDictionary',
      isDetailProject: true,
    },
    {
      name: 'projectClientPause',
      isDesktopSidebar: true,
      labelSidebar: 'projectClientPause',
      iconSidebar: FormatQuoteRounded,
      isDetailProject: true,
    },
    {
      name: 'projectAudioType',
      isDesktopSidebar: true,
      labelSidebar: 'projectAudioType',
      iconSidebar: MovieFilterRoundedIcon,
      isDetailProject: true,
    },
  ];

  const mobileSidebarRoutes = sidebarRoutes.filter(
    (route) => route.isMobileSidebar,
  );
  const desktopSidebarRoutes = sidebarRoutes.filter(
    (route) => route.isDesktopSidebar && !route.isDetailProject,
  );
  const newProjectRoutes = sidebarRoutes.filter(
    (route) => route?.isCreateProject,
  );

  const detailProjectRoutes = sidebarRoutes.filter(
    (route) => route?.isDetailProject,
  );

  return {
    mobileSidebarRoutes,
    desktopSidebarRoutes,
    newProjectRoutes,
    detailProjectRoutes,
  };
};

export default useSidebarRoutes;
